import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, FileText, Send, CheckCircle, Mail, AlertCircle, Eye } from 'lucide-react';
import {
  HubContext,
  transformHubContextToQuote,
  transformHubInteractionToFreelancerSearchResult,
  type HubInteraction,
  type QuoteSnapshot,
} from '../../types/hub';
import { supabase } from '../../lib/supabase';
import { formatTimeAgoWithExactDate } from '../../utils/formatTimeAgo';
import FreelancerCard from '../freelancers/FreelancerCard';
import { Quote } from '../../types/quote';
import FormattedCurrency from '../common/FormattedCurrency';
import BidderDetailsModal from '../quotes/BidderDetailsModal';
import { TimelineItem } from './TimelineItem';
import ProfileAvatar from '../common/ProfileAvatar';

interface InteractionCardProps {
  interaction: HubInteraction;
  currentUserId: string;
  context: HubContext;
}

export const InteractionCard: React.FC<InteractionCardProps> = ({
  interaction,
  currentUserId,
  context,
}) => {
  const isOwnMessage = interaction.source_profile_id === currentUserId;
  const { t } = useTranslation('hub');
  const [expanded, setExpanded] = useState(true);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  const [showQuoteInfo, setShowQuoteInfo] = useState(false);

  const type = interaction.notification_type;
  const payload = interaction.payload || {};
  const message = payload.message as string | undefined;
  const attachments = useMemo(
    () => (payload.attachments as Array<{ path: string; file_name: string; size: number }>) || [],
    [payload.attachments]
  );

  useEffect(() => {
    if (attachments.length === 0) return;

    const loadUrls = async () => {
      const urls: Record<string, string> = {};
      for (const att of attachments) {
        const { data } = await supabase.storage
          .from('hub-attachments')
          .createSignedUrl(att.path, 3600);
        if (data?.signedUrl) {
          urls[att.path] = data.signedUrl;
        }
      }
      setAttachmentUrls(urls);
    };
    loadUrls();
  }, [attachments]);

  const typesWithSentReceivedLabel: Array<typeof type> = [
    'quote_request_received',
    'quote_change_requested',
    'adjustment_submitted',
    'quote_alliance_invited',
    'quote_submitted',
    'child_quote_submitted',
  ];

  let label: string;
  if (type === 'hub_message') {
    label = isOwnMessage
      ? t('yourMessage')
      : t('messageFrom', {
          name: interaction.source_first_name || t('contacts', { ns: 'hub' }),
        });
  } else if (typesWithSentReceivedLabel.includes(type)) {
    const suffix = isOwnMessage ? '_sent' : '_received';
    label = t(`interactionTypes.${type}${suffix}` as const, {
      defaultValue: t(`interactionTypes.${type}` as const, { defaultValue: type }),
    });
  } else {
    label = t(`interactionTypes.${type}` as const, { defaultValue: type });
  }

  const getIcon = (): React.ReactNode => {
    const receivedIcon = <Mail className="h-4 w-4 text-white" />;
    const sentIcon = <Send className="h-4 w-4 text-white" />;

    switch (type) {
      case 'hub_message':
        return <MessageCircle className="h-4 w-4 text-white" />;
      case 'quote_accepted':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'quote_submitted':
      case 'child_quote_submitted':
        return isOwnMessage ? sentIcon : receivedIcon;
      case 'new_job_posted':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'project_cancelled':
        return <AlertCircle className="h-4 w-4 text-white" />;
      case 'adjustment_submitted':
        return isOwnMessage ? sentIcon : receivedIcon;
      case 'quote_request_received':
        return isOwnMessage ? sentIcon : receivedIcon;
      case 'quote_change_requested':
        return isOwnMessage ? sentIcon : receivedIcon;
      case 'quote_request_accepted':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'quote_alliance_invited':
        return isOwnMessage ? sentIcon : receivedIcon;
      case 'quote_alliance_accepted':
        return <CheckCircle className="h-4 w-4 text-white" />;
      case 'quote_alliance_retired':
      case 'quote_alliance_rejected':
        return <AlertCircle className="h-4 w-4 text-white" />;
      default:
        return attachments.length > 0 ? (
          <FileText className="h-4 w-4 text-white" />
        ) : (
          <Send className="h-4 w-4 text-white" />
        );
    }
  };

  const Icon = getIcon();

  const shouldShowGenericMessage =
    Boolean(message) &&
    type !== 'quote_change_requested' &&
    type !== 'quote_submitted' &&
    type !== 'child_quote_submitted' &&
    type !== 'adjustment_submitted';

  const hasContent = shouldShowGenericMessage || attachments.length > 0;
  const hasDetails =
    hasContent ||
    interaction.notification_type === 'quote_request_received' ||
    type === 'quote_submitted' ||
    type === 'child_quote_submitted' ||
    type === 'adjustment_submitted' ||
    type === 'quote_change_requested';

  const foundQuote =
    (context &&
      context.job &&
      context.job.quotes.find(qt => qt.quote_owner.id === interaction.source_profile_id)) ||
    undefined;

  const transformedQuote = foundQuote && transformHubContextToQuote(context, foundQuote);

  const isNewItem = !interaction.read_at && interaction.source_profile_id !== currentUserId;

  const quoteSnapshot = payload.quote_snapshot as QuoteSnapshot | undefined;

  const renderSnapshotBlock = (snapshot: QuoteSnapshot) => (
    <div className="rounded-xl border border-primary/25 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-secondary">{t('quoteSnapshot.title')}</p>
        <p className="text-sm font-semibold text-secondary">
          <FormattedCurrency value={snapshot.total_amount ?? 0} />
        </p>
      </div>

      {(snapshot.tasks?.length ?? 0) > 0 && (
        <div className="mb-2 space-y-1">
          {snapshot.tasks.map(task => (
            <div key={task.id} className="flex items-center justify-between text-xs text-secondary">
              <span className="mr-2 truncate">{task.name}</span>
              <span className="ml-2 whitespace-nowrap font-medium">
                <FormattedCurrency value={task.total_amount ?? 0} />
              </span>
            </div>
          ))}
        </div>
      )}

      {(snapshot.child_quotes?.length ?? 0) > 0 && (
        <div className="mt-3 space-y-1 border-t border-dashed border-gray-200 pt-2">
          <p className="text-xs font-medium text-gray-500">{t('quoteSnapshot.teamSectionLabel')}</p>
          {snapshot.child_quotes.map(child => (
            <div
              key={child.quote_id}
              className="flex items-center justify-between text-xs text-secondary"
            >
              <span className="mr-2 truncate">{t('quoteSnapshot.teamMemberLabel')}</span>
              <span className="ml-2 whitespace-nowrap font-medium">
                <FormattedCurrency value={child.total_amount ?? 0} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderQuoteInfo = (quote: Quote) => {
    const teamMembers = quote.alliances.map(alliance => alliance.invited_freelancer);
    return (
      <div className="relative w-full max-w-4xl rounded-2xl border border-primary/30 bg-white p-4 md:px-6 md:py-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8 w-full">
          <div className="flex items-center gap-4 flex-shrink-0">
            <ProfileAvatar
              profile_id={quote.quote_owner.id}
              first_name={quote.quote_owner.first_name}
              avatar_url={quote.quote_owner.avatar_url}
              className="w-14 h-14"
            />
            <div>
              <p className="font-semibold text-gray-800 text-base md:text-lg">
                {quote.quote_owner.first_name}
              </p>
              <p className="text-sm text-gray-500">{quote.quote_owner.role}</p>
            </div>
          </div>

          {/* Offer – aligned with profile row */}
          <div className="flex flex-col">
            <p className="text-sm text-gray-500">{t('offer', { ns: 'hub' })}</p>
            <p className="font-semibold text-secondary text-sm">
              <FormattedCurrency
                value={foundQuote?.tasks.reduce((sum, task) => sum + task.total_amount, 0) ?? 0}
              />
            </p>
          </div>

          {/* Team – aligned with profile row */}
          {teamMembers.length > 0 && (
            <div className="flex flex-col">
              <span className="text-sm text-gray-500">{t('team', { ns: 'hub' })}</span>
              <div className="mt-1 flex items-center">
                {teamMembers.slice(0, 4).map((member, memberIndex) => (
                  <ProfileAvatar
                    key={`${quote.id}-${member.profile_id}`}
                    profile_id={member.profile_id}
                    first_name={member.first_name}
                    avatar_url={member.avatar_url}
                    className={`${memberIndex === 0 ? '' : 'ml-2'} size-9 border-2 border-white`}
                  />
                ))}
                {teamMembers.length > 4 && (
                  <span className="ml-2 text-sm leading-5">+{teamMembers.length - 4}</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end md:flex-shrink-0 md:pl-4">
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowQuoteInfo(true);
            }}
            className="absolute top-8 right-4 md:static w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors"
            aria-label={t('see_details', { ns: 'hub' })}
          >
            <Eye className="text-primary" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div>
        <div
          className="flex cursor-pointer items-start justify-between gap-2"
          onClick={() => setExpanded(!expanded)}
        >
          <TimelineItem
            icon={Icon}
            title={label}
            subtitle={formatTimeAgoWithExactDate(interaction.created_at)}
            badge={isNewItem ? t('new_label') : undefined}
            expandable={hasDetails}
            expanded={expanded}
          />
        </div>

        {expanded && hasDetails && (
          <div className="mt-3 pl-12">
            {interaction.notification_type === 'quote_request_received' && (
              <div
                className="w-full rounded-xl border border-primary/25 bg-white md:p-4"
                key={interaction.id}
              >
                <FreelancerCard
                  showEyeIcon={true}
                  complete={true}
                  freelancer={transformHubInteractionToFreelancerSearchResult(interaction)}
                  className="w-full border-0 shadow-none"
                />
              </div>
            )}

            {(interaction.notification_type === 'quote_submitted' ||
              interaction.notification_type === 'child_quote_submitted' ||
              interaction.notification_type === 'adjustment_submitted') &&
              (quoteSnapshot ? (
                <div className="mb-3">{renderSnapshotBlock(quoteSnapshot)}</div>
              ) : (
                foundQuote && <div>{transformedQuote && renderQuoteInfo(transformedQuote)}</div>
              ))}

            {interaction.notification_type === 'quote_change_requested' && message && (
              <p className="mb-3 text-sm text-secondary whitespace-pre-wrap">{message}</p>
            )}

            {hasContent && (
              <>
                {shouldShowGenericMessage && (
                  <p
                    className={`w-full whitespace-pre-wrap rounded-xl border border-primary/25 text-secondary md:p-4 ${
                      isOwnMessage ? 'bg-[#F3F5F6] border-0' : 'bg-white'
                    }`}
                  >
                    {message}
                  </p>
                )}
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    {attachments.map(att => (
                      <a
                        key={att.path}
                        href={attachmentUrls[att.path]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-primary hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{att.file_name}</span>
                        <span className="text-gray-500">({(att.size / 1024).toFixed(1)} KB)</span>
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showQuoteInfo && transformedQuote && (
        <BidderDetailsModal
          job={context.job}
          quote={transformedQuote}
          onClose={() => setShowQuoteInfo(false)}
        />
      )}
    </>
  );
};
